import { createRouter, createWebHistory } from 'vue-router';
import { useShopStore } from '@/stores/shop.store';
import { useKDSStore } from '@/stores/kds.store';
import { useLicenseStore } from '@/stores/license.store';
import { useAuthStore } from '@/stores/auth.store';
import i18n from '@/plugins/i18n';
import axios from 'axios';
import Cookies from 'js-cookie';
import Auth from '@/services/auth.service';
import Branches from '@/components/branches';
import Login from '@/components/login.vue';
import ChangePassword from '@/components/change-password.vue';
import Register from '@/components/register.vue';
import Products from '@/components/products';
import Modifiers from '@/components/product-modifiers';
import Attributes from '@/components/product-attributes';
import ProductCategories from '@/components/product-categories';
import Zones from '@/components/zones';
import Tables from '@/components/tables';
import Expenses from '@/components/expenses';
import ExpenseCategories from '@/components/expense-categories';
import DeliveryPlatforms from '@/components/delivery-platforms';
import CustomerGroups from '@/components/customer-groups';
import Inventories from '@/components/inventories';
import InventoryProductionBatches from '@/components/inventory-production-batches';
import Recipes from '@/components/recipes';
import InventoryGoodsReceipts from '@/components/inventory-goods-receipts';
import StockAdjustments from '@/components/stock-adjustments';
import Supplier from '@/components/suppliers';
import Dashboards from '@/components/dashboards';
import Reports from '@/components/reports';
import ReportSalesSummary from '@/components/reports/ReportSalesSummary.vue';
import ReportSalesByHour from '@/components/reports/ReportSalesByHour.vue';
import ReportSalesByBranch from '@/components/reports/ReportSalesByBranch.vue';
import ReportPlatformSales from '@/components/reports/ReportPlatformSales.vue';
import ReportProductSales from '@/components/reports/ReportProductSales.vue';
import ReportCategorySales from '@/components/reports/ReportCategorySales.vue';
import ReportPaymentMethods from '@/components/reports/ReportPaymentMethods.vue';
import ReportDiscounts from '@/components/reports/ReportDiscounts.vue';
import ReportPromotionPerformance from '@/components/reports/ReportPromotionPerformance.vue';
import ReportVatSalesList from '@/components/reports/ReportVatSalesList.vue';
import ReportTaxInvoiceRegister from '@/components/reports/ReportTaxInvoiceRegister.vue';
import ReportVatPurchaseList from '@/components/reports/ReportVatPurchaseList.vue';
import ReportProfit from '@/components/reports/ReportProfit.vue';
import ReportExpense from '@/components/reports/ReportExpense.vue';
import ReportGoodsReceipt from '@/components/reports/ReportGoodsReceipt.vue';
import ReportStaffPerformance from '@/components/reports/ReportStaffPerformance.vue';
import ReportCancelledOrders from '@/components/reports/ReportCancelledOrders.vue';
import ReportStockMovement from '@/components/reports/ReportStockMovement.vue';
import ReportLowStock from '@/components/reports/ReportLowStock.vue';
import ReportWaste from '@/components/reports/ReportWaste.vue';
import ReportMaterialUsage from '@/components/reports/ReportMaterialUsage.vue';
import Users from '@/components/users';
import Roles from '@/components/roles';
import ShiftManagement from '@/components/shift-management';
import TEST from '@/components/tests';
import Customers from '@/components/customers';
import LoyaltyPrograms from '@/components/loyalty/programs';
import Promotions from '@/components/promotions';
import Settings from '@/components/settings';
import Profile from '@/components/profile';
import KdsStations from '@/components/kds-stations';
import Bills from '@/components/bills';
import TaxInvoices from '@/components/tax-invoices';
import Reservations from '@/components/reservations';
import PageNotFound from '@/components/PageNotFound.vue';
import POSRegister from '@/components/pos-register';
import KDSRegister from '@/components/kds-register';
import KDSStationSelect from '@/components/kds-register/StationSelect.vue';
import KDSDisplay from '@/components/kds/display';
import POSTable from '@/components/pos/tables';
import POSSessions from '@/components/pos/sessions';
import POSBills from '@/components/pos/bills';
import POSReservations from '@/components/pos/reservations';
import MenuAndCart from '@/components/pos/menu-and-cart';
import POSPlatformOrders from '@/components/pos/platform-orders';
import CustomerService from '@/components/service';
import PreviewProduct from '@/components/preview-product';

const titleSuffix = 'Back office';
let AuthorizationLayout = 'authorization-layout';
let AdminLayout = 'admin-layout';
let POSLayout = 'p-o-s-layout';
let KDSLayout = 'k-d-s-layout';
let ServiceLayout = 'service-layout';

const routes = [
  {
    path: '/',
    name: 'dashboards',
    component: Dashboards,
    meta: {
      layout: AdminLayout,
      title: 'Dashboards',
      // breadcrumb: [
      //   { name: 'Dashboards' },
      // ],
      suffix: titleSuffix
    }
  },
  {
    path: '/pos-register',
    name: 'pos-register',
    component: POSRegister,
    meta: {
      layout: AdminLayout,
      title: 'POS (Point of Sale)',
      breadcrumb: [
        { name: 'POS (Point of Sale)' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/kds-register',
    name: 'kds-register',
    component: KDSRegister,
    meta: {
      feature: 'feature_kds',   // แพ็กไม่มี → ซ่อนเมนู + หน้าขึ้นกุญแจ (layout)
      layout: AdminLayout,
      title: 'KDS (Kitchen Display System)',
      breadcrumb: [
        { name: 'KDS (Kitchen Display System)' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/kds-register/:branchId/stations',
    name: 'kds.station-select',
    component: KDSStationSelect,
    meta: {
      feature: 'feature_kds',   // แพ็กไม่มี → ซ่อนเมนู + หน้าขึ้นกุญแจ (layout)
      layout: AuthorizationLayout,   // full-screen แยก (ไม่มี sidebar admin) เหมือนเข้าโหมด KDS
      title: 'Select Station',
      suffix: titleSuffix
    }
  },
  {
    path: '/branches',
    name: 'branches',
    component: Branches,
    meta: {
      layout: AdminLayout,
      title: 'Branch Management',
      // breadcrumb: [
      //   { name: 'Branch Management' },
      // ],
      suffix: titleSuffix
    }
  },
  {
    path: '/inventories',
    name: 'inventories',
    component: Inventories,
    meta: {
      feature: 'feature_inventory',   // แพ็กไม่มี → ซ่อนเมนู + หน้าขึ้นกุญแจ (layout)
      layout: AdminLayout,
      title: 'Raw Material Management',
      breadcrumb: [
        // { name: 'Goods Receipts', link: '/inventory-goods-receipts' },
        { name: 'Raw Material Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/inventory-goods-receipts',
    name: 'inventory-goods-receipts',
    component: InventoryGoodsReceipts,
    meta: {
      feature: 'feature_inventory',   // แพ็กไม่มี → ซ่อนเมนู + หน้าขึ้นกุญแจ (layout)
      layout: AdminLayout,
      title: 'Goods Receipt Management',
      breadcrumb: [
        { name: 'Goods Receipt Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/stock-adjustments',
    name: 'stock-adjustments',
    component: StockAdjustments,
    meta: {
      feature: 'feature_inventory',   // แพ็กไม่มี → ซ่อนเมนู + หน้าขึ้นกุญแจ (layout)
      layout: AdminLayout,
      title: 'Stock Management',
      breadcrumb: [
        { name: 'Stock Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/production-batches',
    name: 'production-batches',
    component: InventoryProductionBatches,
    meta: {
      feature: 'feature_inventory',   // แพ็กไม่มี → ซ่อนเมนู + หน้าขึ้นกุญแจ (layout)
      layout: AdminLayout,
      title: 'Production Batch Management',
      breadcrumb: [
        { name: 'Production Batch Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/recipes',
    name: 'recipes',
    component: Recipes,
    meta: {
      feature: 'feature_inventory',   // แพ็กไม่มี → ซ่อนเมนู + หน้าขึ้นกุญแจ (layout)
      layout: AdminLayout,
      title: 'Recipe Management',
      breadcrumb: [
        { name: 'Recipe Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/supplier',
    name: 'supplier',
    component: Supplier,
    meta: {
      feature: 'feature_inventory',   // แพ็กไม่มี → ซ่อนเมนู + หน้าขึ้นกุญแจ (layout)
      layout: AdminLayout,
      title: 'Supplier Management',
      breadcrumb: [
        { name: 'Supplier Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/products',
    name: 'products',
    component: Products,
    meta: {
      layout: AdminLayout,
      title: 'Product Management',
      breadcrumb: [
        { name: 'Product Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/modifiers',
    name: 'modifiers',
    component: Modifiers,
    meta: {
      layout: AdminLayout,
      title: 'Option Group Management',
      breadcrumb: [
        { name: 'Option Group Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/product-categories',
    name: 'product-categories',
    component: ProductCategories,
    meta: {
      layout: AdminLayout,
      title: 'Category Management',
      breadcrumb: [
        { name: 'Settings', link: '/settings' },
        { name: 'Category Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/attributes',
    name: 'attributes',
    component: Attributes,
    meta: {
      layout: AdminLayout,
      title: 'Product Variant Management',
      breadcrumb: [
        { name: 'Product Variant Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/customers',
    name: 'customers',
    component: Customers,
    meta: {
      layout: AdminLayout,
      title: 'Customer Membership',
      breadcrumb: [
        { name: 'Customer Membership' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/customer-groups',
    name: 'customer-groups',
    component: CustomerGroups,
    meta: {
      feature: 'feature_customer_groups',   // แพ็กไม่มี → ซ่อนเมนู + หน้าขึ้นกุญแจ (layout)
      layout: AdminLayout,
      title: 'Customer Group Management',
      breadcrumb: [
        { name: 'Customer Group Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/loyalty/programs',
    name: 'loyalty-programs',
    component: LoyaltyPrograms,
    meta: {
      feature: 'feature_loyalty',   // แพ็กไม่มี → ซ่อนเมนู + หน้าขึ้นกุญแจ (layout)
      layout: AdminLayout,
      title: 'Loyalty Program Management',
      breadcrumb: [
        { name: 'Loyalty Program Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/promotions',
    name: 'promotions',
    component: Promotions,
    meta: {
      feature: 'feature_promotions',   // แพ็กไม่มี → ซ่อนเมนู + หน้าขึ้นกุญแจ (layout)
      layout: AdminLayout,
      title: 'Promotion Management',
      breadcrumb: [
        { name: 'Promotion Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/settings',
    name: 'settings',
    component: Settings,
    meta: {
      layout: AdminLayout,
      title: 'Settings',
      breadcrumb: [
        { name: 'Settings' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/profile',
    name: 'profile',
    component: Profile,
    meta: {
      layout: AdminLayout,
      title: 'Edit Profile',
      breadcrumb: [
        { name: 'Edit Profile' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/kds-stations',
    name: 'kds-stations',
    component: KdsStations,
    meta: {
      feature: 'feature_kds',   // แพ็กไม่มี → ซ่อนเมนู + หน้าขึ้นกุญแจ (layout)
      layout: AdminLayout,
      title: 'KDS Stations',
      breadcrumb: [
        { name: 'Settings', link: '/settings' },
        { name: 'KDS Stations' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/bill-management',
    name: 'bill-management',
    component: Bills,
    meta: {
      layout: AdminLayout,
      title: 'Bill Management',
      breadcrumb: [
        { name: 'Bill Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/tax-invoices',
    name: 'tax-invoices',
    component: TaxInvoices,
    meta: {
      feature: 'feature_tax_full',   // แพ็กไม่มี → ซ่อนเมนู + หน้าขึ้นกุญแจ (layout)
      layout: AdminLayout,
      title: 'Tax Invoice Management',
      breadcrumb: [
        { name: 'Tax Invoice Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/reservations',
    name: 'reservations',
    component: Reservations,
    meta: {
      feature: 'feature_reservations',   // แพ็กไม่มี → ซ่อนเมนู + หน้าขึ้นกุญแจ (layout)
      layout: AdminLayout,
      title: 'Reservations',
      breadcrumb: [
        { name: 'Reservations' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/zones',
    name: 'zones',
    component: Zones,
    meta: {
      layout: AdminLayout,
      title: 'Zone Management',
      breadcrumb: [
        { name: 'Zone Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/expenses',
    name: 'expenses',
    component: Expenses,
    meta: {
      feature: 'feature_expenses',   // แพ็กไม่มี → ซ่อนเมนู + หน้าขึ้นกุญแจ (layout)
      layout: AdminLayout,
      title: 'Expense Management',
      breadcrumb: [
        { name: 'Expense Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/expense-categories',
    name: 'expense-categories',
    component: ExpenseCategories,
    meta: {
      feature: 'feature_expenses',   // แพ็กไม่มี → ซ่อนเมนู + หน้าขึ้นกุญแจ (layout)
      layout: AdminLayout,
      title: 'Expense Category Management',
      breadcrumb: [
        { name: 'Expense Category Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/delivery-platforms',
    name: 'delivery-platforms',
    component: DeliveryPlatforms,
    meta: {
      feature: 'feature_delivery_platforms',   // แพ็กไม่มี → ซ่อนเมนู + หน้าขึ้นกุญแจ (layout)
      layout: AdminLayout,
      title: 'Delivery Channel Management',
      breadcrumb: [
        { name: 'Delivery Channel Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/tables',
    name: 'tables',
    component: Tables,
    meta: {
      layout: AdminLayout,
      title: 'Table Management',
      breadcrumb: [
        { name: 'Table Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/users',
    name: 'users',
    component: Users,
    meta: {
      layout: AdminLayout,
      title: 'User Management',
      breadcrumb: [
        { name: 'User Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/roles',
    name: 'roles',
    component: Roles,
    meta: {
      layout: AdminLayout,
      title: 'Role Management',
      breadcrumb: [
        { name: 'Role Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/shift-management',
    name: 'shift-management',
    component: ShiftManagement,
    meta: {
      layout: AdminLayout,
      title: 'Shift Management',
      breadcrumb: [
        { name: 'Shift Management' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/reports',
    name: 'reports',
    component: Reports,
    meta: {
      layout: AdminLayout,
      title: 'Reports',
      breadcrumb: [
        { name: 'Reports' },
      ],
      suffix: titleSuffix
    }
  },
  {
    path: '/reports/sales-summary',
    name: 'report.sales-summary',
    component: ReportSalesSummary,
    meta: {
      layout: AdminLayout,
      title: 'Sales Summary',
      breadcrumb: [
        { name: 'Reports', link: '/reports' },
        { name: 'Sales Summary' }
      ],
      suffix: titleSuffix }
    },
  { path: '/reports/sales-by-hour',    name: 'report.sales-by-hour',    component: ReportSalesByHour,     meta: { layout: AdminLayout, title: 'Sales by Hour',    breadcrumb: [{ name: 'Reports', link: '/reports' }, { name: 'Sales by Hour' }],    suffix: titleSuffix } },
  { path: '/reports/sales-by-branch',  name: 'report.sales-by-branch',  component: ReportSalesByBranch,   meta: { layout: AdminLayout, title: 'Sales by Branch',  breadcrumb: [{ name: 'Reports', link: '/reports' }, { name: 'Sales by Branch' }],  suffix: titleSuffix } },
  { path: '/reports/platform-sales',  name: 'report.platform-sales',  component: ReportPlatformSales,   meta: { layout: AdminLayout, title: 'Platform Sales',  breadcrumb: [{ name: 'Reports', link: '/reports' }, { name: 'Platform Sales' }],  suffix: titleSuffix } },
  { path: '/reports/product-sales',    name: 'report.product-sales',    component: ReportProductSales,    meta: { layout: AdminLayout, title: 'Product Sales',    breadcrumb: [{ name: 'Reports', link: '/reports' }, { name: 'Product Sales' }],    suffix: titleSuffix } },
  { path: '/reports/category-sales',   name: 'report.category-sales',   component: ReportCategorySales,   meta: { layout: AdminLayout, title: 'Category Sales',   breadcrumb: [{ name: 'Reports', link: '/reports' }, { name: 'Category Sales' }],   suffix: titleSuffix } },
  { path: '/reports/payment-methods',  name: 'report.payment-methods',  component: ReportPaymentMethods,  meta: { layout: AdminLayout, title: 'Payment Methods',  breadcrumb: [{ name: 'Reports', link: '/reports' }, { name: 'Payment Methods' }],  suffix: titleSuffix } },
  { path: '/reports/discount-report',  name: 'report.discount-report',  component: ReportDiscounts,       meta: { layout: AdminLayout, title: 'Discount Report',  breadcrumb: [{ name: 'Reports', link: '/reports' }, { name: 'Discount Report' }],  suffix: titleSuffix } },
  { path: '/reports/promotion-performance', name: 'report.promotion-performance', component: ReportPromotionPerformance, meta: { layout: AdminLayout, title: 'Promotion & Loyalty Performance', breadcrumb: [{ name: 'Reports', link: '/reports' }, { name: 'Promotion & Loyalty Performance' }], suffix: titleSuffix } },
  { path: '/reports/vat-sales-list',   name: 'report.vat-sales-list',   component: ReportVatSalesList,    meta: { layout: AdminLayout, title: 'VAT Sales List',   breadcrumb: [{ name: 'Reports', link: '/reports' }, { name: 'VAT Sales List' }],   suffix: titleSuffix } },
  { path: '/reports/tax-invoice-register', name: 'report.tax-invoice-register', component: ReportTaxInvoiceRegister, meta: { layout: AdminLayout, title: 'Tax Invoice Register', breadcrumb: [{ name: 'Reports', link: '/reports' }, { name: 'Tax Invoice Register' }], suffix: titleSuffix } },
  { path: '/reports/vat-purchase-list', name: 'report.vat-purchase-list', component: ReportVatPurchaseList, meta: { layout: AdminLayout, title: 'VAT Purchase List', breadcrumb: [{ name: 'Reports', link: '/reports' }, { name: 'VAT Purchase List' }], suffix: titleSuffix } },
  { path: '/reports/profit-report',    name: 'report.profit-report',    component: ReportProfit,          meta: { layout: AdminLayout, title: 'Profit & Margin',  breadcrumb: [{ name: 'Reports', link: '/reports' }, { name: 'Profit & Margin' }],  suffix: titleSuffix } },
  { path: '/reports/expense-report',   name: 'report.expense-report',   component: ReportExpense,         meta: { layout: AdminLayout, title: 'Expense Report',   breadcrumb: [{ name: 'Reports', link: '/reports' }, { name: 'Expense Report' }],   suffix: titleSuffix } },
  { path: '/reports/goods-receipt-report', name: 'report.goods-receipt-report', component: ReportGoodsReceipt, meta: { layout: AdminLayout, title: 'Goods Receipt Report', breadcrumb: [{ name: 'Reports', link: '/reports' }, { name: 'Goods Receipt Report' }], suffix: titleSuffix } },
  { path: '/reports/staff-performance', name: 'report.staff-performance', component: ReportStaffPerformance, meta: { layout: AdminLayout, title: 'Staff Performance', breadcrumb: [{ name: 'Reports', link: '/reports' }, { name: 'Staff Performance' }], suffix: titleSuffix } },
  { path: '/reports/cancelled-orders', name: 'report.cancelled-orders', component: ReportCancelledOrders, meta: { layout: AdminLayout, title: 'Cancelled Orders', breadcrumb: [{ name: 'Reports', link: '/reports' }, { name: 'Cancelled Orders' }], suffix: titleSuffix } },
  { path: '/reports/material-usage',   name: 'report.material-usage',   component: ReportMaterialUsage,   meta: { layout: AdminLayout, title: 'Material Usage',   breadcrumb: [{ name: 'Reports', link: '/reports' }, { name: 'Material Usage' }],   suffix: titleSuffix } },
  { path: '/reports/stock-movement',   name: 'report.stock-movement',   component: ReportStockMovement,   meta: { layout: AdminLayout, title: 'Stock Movement',   breadcrumb: [{ name: 'Reports', link: '/reports' }, { name: 'Stock Movement' }],   suffix: titleSuffix } },
  { path: '/reports/low-stock',        name: 'report.low-stock',        component: ReportLowStock,        meta: { layout: AdminLayout, title: 'Low Stock Alert',  breadcrumb: [{ name: 'Reports', link: '/reports' }, { name: 'Low Stock Alert' }],  suffix: titleSuffix } },
  { path: '/reports/waste-report',     name: 'report.waste-report',     component: ReportWaste,           meta: { layout: AdminLayout, title: 'Waste Report',     breadcrumb: [{ name: 'Reports', link: '/reports' }, { name: 'Waste Report' }],     suffix: titleSuffix } },
  {
    path: '/kds/display',
    name: 'kds.display',
    component: KDSDisplay,
    meta: {
      feature: 'feature_kds',   // แพ็กไม่มี → ซ่อนเมนู + หน้าขึ้นกุญแจ (layout)
      layout: 'KDSLayout',
      title: 'KDS Display',
      suffix: 'KDS'
    }
  },
  {
    path: '/pos/table',
    name: 'pos.table',
    component: POSTable,
    meta: {
      layout: POSLayout,
      title: 'Tables',
      breadcrumb: [
        { name: 'Tables & Zones' },
      ],
      suffix: 'POS'
    }
  },
  {
    path: '/pos/platform-orders',
    name: 'pos.platform-orders',
    component: POSPlatformOrders,
    meta: {
      feature: 'feature_delivery_platforms',   // แพ็กไม่มี → ซ่อนเมนู + หน้าขึ้นกุญแจ (layout)
      layout: POSLayout,
      title: 'Platform Orders',
      breadcrumb: [
        { name: 'Platform Orders' },
      ],
      suffix: 'POS'
    }
  },
  {
    path: '/pos/sessions/:token',
    name: 'pos.sessions.detail',
    component: MenuAndCart,
    meta: {
      layout: POSLayout,
      // Pin the POS shell to the viewport so only the product list scrolls
      // (see POSLayout's .pos-fullheight styles). Other POS pages keep normal
      // document scroll.
      fullHeight: true,
      title: 'Menu And Cart',
      breadcrumb: [
        { name: 'Menu And Cart' },
      ],
      suffix: 'POS'
    }
  },
  {
    path: '/pos/sessions',
    name: 'pos.sessions',
    component: POSSessions,
    meta: {
      layout: POSLayout,
      title: 'Service Sessions',
      breadcrumb: [
        { name: 'Service Sessions' },
      ],
      suffix: 'POS'
    }
  },
  {
    path: '/pos/bills',
    name: 'pos.bills',
    component: POSBills,
    meta: {
      layout: POSLayout,
      title: "Today's Bills",
      breadcrumb: [
        { name: "Today's Bills" },
      ],
      suffix: 'POS'
    }
  },
  {
    path: '/pos/reservations',
    name: 'pos.reservations',
    component: POSReservations,
    meta: {
      feature: 'feature_reservations',   // แพ็กไม่มี → ซ่อนเมนู + หน้าขึ้นกุญแจ (layout)
      layout: POSLayout,
      title: 'Reservations',
      breadcrumb: [
        { name: 'Reservations' },
      ],
      suffix: 'POS'
    }
  },
  {
    path: '/service/:token',
    name: 'service',
    component: CustomerService,
    meta: {
      layout: ServiceLayout,
      title: 'Chongern',
      suffix: 'Service'
    }
  },
  {
    path: '/preview-product',
    name: 'preview-product',
    component: PreviewProduct,
    meta: {
      // ServiceLayout is a pass-through (no chrome) — perfect for the
      // public preview page which renders its own header.
      layout: ServiceLayout,
      title: 'Menu Preview',
      suffix: 'Preview'
    }
  },
  {
    // Branch-scoped variant of the preview page. Same component, but the
    // :code (branch code) makes it show only that branch's on-sale menu
    // (branch_products.is_active) with out-of-stock flags. The plain
    // /preview-product route above stays the full all-products catalogue.
    path: '/preview-product/:code',
    name: 'preview-product-branch',
    component: PreviewProduct,
    meta: {
      layout: ServiceLayout,
      title: 'Menu Preview',
      suffix: 'Preview'
    }
  },
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: {
      layout: AuthorizationLayout,
      title: 'Login',
      suffix: titleSuffix
    }
  },
  {
    // ตั้งรหัสผ่านใหม่ — เต็มจอไม่มี sidebar เพราะเป็นหน้าที่ "ต้องผ่านก่อน" ตอนโดน Owner รีเซ็ตรหัสมา
    // (api/docs/password-reset.md §5.2) เข้าเองเพื่อเปลี่ยนรหัสเฉย ๆ ก็ได้
    path: '/change-password',
    name: 'change-password',
    component: ChangePassword,
    meta: {
      layout: AuthorizationLayout,
      title: 'Set a New Password',
      suffix: titleSuffix
    }
  },
  {
    path: '/register',
    name: 'register',
    component: Register,
    meta: {
      layout: AuthorizationLayout,
      title: 'Register',
      suffix: titleSuffix
    }
  },
  {
    path: '/test/:id',
    name: 'test',
    component: TEST,
    meta: {
      layout: AuthorizationLayout,
      title: 'TEST',
      suffix: titleSuffix
    }
  },
  {
    path: '/:catchAll(.*)',
    name: 'Not Found',
    component: PageNotFound,
    meta: {
      layout: AdminLayout,
      title: 'Not Found',
      suffix: titleSuffix
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

axios.interceptors.request.use((request) => {
  return request;
});

/**
 * แพ็กเปลี่ยนกลางคัน (X-License-Plan ไม่ตรงกับที่จำไว้) → ดึง features ชุดใหม่จาก /v1/profile
 * เฉพาะตอนล็อกอินอยู่ และไม่ใช่ response ของ login เอง — ตอนนั้น token ยังไม่ถูกเก็บ
 * ยิง profile ไปจะได้ 401 แล้วโดน interceptor ข้างล่างเด้งออกทั้งที่เพิ่งล็อกอิน
 * (หน้า QR ลูกค้าไม่มี token → ไม่เข้าเงื่อนไขเอง)
 */
const syncPlan = (response) => {
  if (!Cookies.get('token')) return;
  if (String(response?.config?.url || '').includes('/v1/auth/login')) return;
  useLicenseStore().checkPlan(response?.headers?.['x-license-plan']);
};

/**
 * error ของแพ็ก → เปลี่ยน message เป็น "ข้อความที่แปลแล้ว" ตามภาษาปัจจุบัน (ไม่ใช่คีย์)
 * เพราะ component ส่วนใหญ่ toast `err.response.data.message` ตรง ๆ ไม่ได้ผ่าน `$t()`
 * — ถ้าใส่เป็นคีย์ หน้าพวกนั้นจะโชว์ "_plan.Not included in your plan" ดิบ ๆ (เคยเจอจริง)
 * หน้าที่ครอบ `$t()` อยู่แล้วก็ยังได้ข้อความเดิม (หาคีย์ไม่เจอ = คืนข้อความนั้นกลับมา)
 * หน้าที่ต้องการ UX เฉพาะ (รายงาน / สาขา / สถานี KDS) ยังเช็ค code เองได้ตามเดิม
 */
const PLAN_ERROR_MESSAGES = {
  ERROR_FEATURE_NOT_AVAILABLE: '_plan.Not included in your plan',
  ERROR_PLAN_LIMIT_REACHED: '_plan.Plan limit reached',
};

// สถานะอายุการใช้งานติดมากับ header ของทุก response (X-License-*) — อ่านที่นี่ที่เดียว
// ทั้งแอปจึงรู้สถานะล่าสุดโดยไม่ต้องยิง API เพิ่มสักเส้น แม้แต่จอที่เปิดค้างข้ามวัน
// (ดู ../silver-spoon-api/docs/license-expiry.md §4.2)
const isServicesUrl = (response) => String(response?.config?.url || '').includes('/v1/services/');

axios.interceptors.response.use((response) => {
  useLicenseStore().applyHeaders(response.headers);
  syncPlan(response);
  // หน้า QR ลูกค้า: ตอบสำเร็จ = แพ็กมี feature_qr_ordering (อัปเกรดแล้ว / เคยพลาด) → เปิดจอกลับ
  if (isServicesUrl(response) && useLicenseStore().servicesUnavailable) {
    useLicenseStore().setServicesUnavailable(false);
  }
  return response;
}, (error) => {
  if (error.response != undefined) {
    useLicenseStore().applyHeaders(error.response.headers);
    if (error.response.status != 401) syncPlan(error.response);

    // หน้า QR ลูกค้า: แพ็กไม่มี feature_qr_ordering → จอกลาง ๆ แบบตอนล็อก (service/index.vue)
    if (error.response.status == 403 && error.response.data?.code == 'ERROR_FEATURE_NOT_AVAILABLE' && isServicesUrl(error.response)) {
      useLicenseStore().setServicesUnavailable(true);
    }

    const planMessage = PLAN_ERROR_MESSAGES[error.response.data?.code];
    if (planMessage) error.response.data.message = i18n.global.t(planMessage);

    // เชื่อทันทีไม่ต้องรอ header รอบถัดไป — เส้นตายสุดท้ายถ้า header หลุด
    if (error.response.status == 403 && error.response.data && error.response.data.code == 'ERROR_LICENSE_EXPIRED') {
      useLicenseStore().markLocked(error.response.data.expires_on);
    }
  }

  if (error.response != undefined && error.response.status == 401 && error.response.data && error.response.data.code == 'POS_UNAUTHORIZED') {
    useShopStore().clearShopState();
    window.location.href = process.env.VUE_APP_URL;
  } else if (error.response != undefined && error.response.status == 401) {
    Auth.logout();
    window.location.href = process.env.VUE_APP_URL
  }
  return Promise.reject(error);
});

router.beforeEach((to, from, next) => {
  const publicPages = [
    'login',
    'register',
    'test',
    'service',
    'preview-product',
    'preview-product-branch'
  ];
  const authRequired = !publicPages.includes(to.name);
  const token = Cookies.get('token');

  const POSPages = [
    'pos.table',
    'pos.sessions',
    'pos.bills',
    'pos.reservations'
  ]
  const tokenPOS = useShopStore().shiftId ? true : false;

  const KDSPages = [
    'kds.display'
  ]
  const tokenKDS = useKDSStore().id ? true : false;

  // Auth-only pages (login/register) bounce already-logged-in users home.
  // `service` is public AND should remain reachable to logged-in admins for
  // dev/testing, so it's excluded from that redirect.
  const bounceWhenAuthed = ['login', 'register'];
  if (bounceWhenAuthed.includes(to.name) && token) {
    window.location.href = process.env.VUE_APP_URL
  } else if (!tokenPOS && POSPages.includes(to.name) || !tokenKDS && KDSPages.includes(to.name)) {
    window.location.href = process.env.VUE_APP_URL
  } else if (authRequired && !token) {
    next(false);
    window.location.href = process.env.VUE_APP_URL + '/login'
  } else if (
    // โดน Owner รีเซ็ตรหัสผ่านมา = ต้องตั้งรหัสของตัวเองก่อน ถึงจะเข้าหน้าอื่นได้
    // (api/docs/password-reset.md §5.2 — ธงมาจาก must_change_password ที่ LoginResource ส่งมา)
    token
    && authRequired
    && to.name !== 'change-password'
    && useAuthStore().user?.must_change_password
  ) {
    return next({ name: 'change-password' });
  }
  next();
});

export default router
